import React, { useState, useCallback } from 'react';
import BasePracticeMode from '../../common/practice/BasePracticeMode';
import AlgorithmSelector from '../../common/algorithm/AlgorithmSelector';
import { getRandomCase, getAlgorithmSet } from '../../../constants/algorithms';
import { PRACTICE_MODES } from '../../../constants/modes';
import { logDebug, logError } from '../../../utils/logging';
import { useLocation } from 'react-router-dom';
import SlowestAlgorithmsPanel from '../../statistics/panels/SlowestAlgorithmsPanel';

export default function RandomPLLExecution() {
  const pllSet = getAlgorithmSet('PLL');
  const location = useLocation();

  const [selectedAlgorithms, setSelectedAlgorithms] = useState(() => {
    if (location.state?.selectedAlgorithms) {
      return location.state.selectedAlgorithms;
    }
    return Object.keys(pllSet.cases);
  });

  const [currentCase, setCurrentCase] = useState(() => {
    return getRandomCase('PLL', PRACTICE_MODES.PLL.EXECUTION, selectedAlgorithms);
  });
  const [previousCase, setPreviousCase] = useState(null);
  const [showSolution, setShowSolution] = useState(false);

  const handleSolveComplete = useCallback((time) => {
    if (typeof time === 'undefined' || time === null) {
      logError('Invalid time value:', time);
      return;
    }

    logDebug('Solve completed:', {
      algorithm: currentCase.id,
      time,
      mode: PRACTICE_MODES.PLL.EXECUTION
    });

    setPreviousCase(currentCase);
    const nextCase = getRandomCase('PLL', PRACTICE_MODES.PLL.EXECUTION, selectedAlgorithms);
    if (nextCase) {
      setCurrentCase(nextCase);
      setShowSolution(false);
    }
  }, [currentCase, selectedAlgorithms]);

  const handleSkipCase = useCallback(() => {
    handleSolveComplete();
  }, [handleSolveComplete]);

  const handleRedoCase = useCallback(() => {
    if (previousCase) {
      setCurrentCase(previousCase);
      setShowSolution(false);
    }
  }, [previousCase]);

  const handleAlgorithmSelection = (selected) => {
    setSelectedAlgorithms(selected);
    const nextCase = getRandomCase('PLL', PRACTICE_MODES.PLL.EXECUTION, selected);
    if (nextCase) {
      setCurrentCase(nextCase);
      setShowSolution(false);
    }
  };

  const renderContent = () => (
    <div className="algorithm-display">
      <AlgorithmSelector
        algorithms={pllSet}
        selectedAlgorithms={selectedAlgorithms}
        onChange={handleAlgorithmSelection}
        type="PLL"
      />
      
      <h2>{currentCase.name}</h2>
      <div className="setup-moves">
        <h3>Setup Moves:</h3>
        <p className="algorithm-text">{currentCase.setupAlgorithm}</p>
      </div>
      {showSolution && (
        <div className="solution-moves">
          <h3>Solution:</h3>
          <p className="algorithm-text">{currentCase.algorithm}</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <BasePracticeMode
        mode={PRACTICE_MODES.PLL.EXECUTION}
        title="Random PLL Practice"
        algorithmId={currentCase.id}
        onComplete={handleSolveComplete}
        onSkipCase={handleSkipCase}
        onRedoCase={handleRedoCase}
        renderContent={renderContent}
        showSolution={showSolution}
        onToggleSolution={() => setShowSolution(!showSolution)}
      />
      <SlowestAlgorithmsPanel 
        mode={PRACTICE_MODES.PLL.EXECUTION}
        title="Slowest PLL Execution"
      />
    </>
  );
} 